import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SeatsioEventManager, SelectableObject } from '@seatsio/seatsio-react';
import type { EventManagerMode } from '@seatsio/seatsio-types';

import useGetPartnerData from 'features/common/hooks/useGetPartnerData';
import type { SeatsSelectionChartProps } from 'features/common/hooks/useSeatsSelection';

import styles from './PunchupSeatsWidget.module.css';
import { SEATS_COLORS, SEATS_STYLE, toSeatsLanguage, toSeatsRegion } from './seatsTheme';

/**
 * The full set of seats.io operating modes. The dashboard's seats accordion exposes a
 * trimmed subset (`static`, `manageChannels`, `manageTableBooking`); other green-zone
 * surfaces pin a single mode (`manageForSaleConfig` for Pools, `select` for Send Tickets).
 */
export type PunchupSeatsMode = Exclude<EventManagerMode, 'filterSections' | 'createOrder' | 'editOrder'>;

/**
 * Per-object color override callback. Forwarded to seats.io's `objectColor`. Available
 * in every event-manager mode via `ExtractedEventManagerProps`. Use to grey out objects
 * outside a target category when the user has narrowed their intent (e.g. via a radio).
 */
export type SeatsObjectColorFn = (
	object: SelectableObject,
	defaultColor: string,
	extraConfig: Record<string, unknown>
) => string;

/**
 * Per-object selectability callback. Forwarded to seats.io's `isObjectSelectable`.
 * **Available only in `select` mode** per the SDK type — passing this prop in any other
 * mode is silently ignored by the widget. Use to hard-gate selection at the SDK level
 * (e.g. constrain Send-Tickets selection to a single ticket-type's category).
 */
export type SeatsIsObjectSelectableFn = (object: SelectableObject) => boolean;

interface PunchupSeatsWidgetProps {
	/** seats.io event key. The widget is rendered only when this is truthy. */
	eventKey: string | null | undefined;
	/** Operating mode for the embedded widget. */
	mode: PunchupSeatsMode;
	/**
	 * Output of `useSeatsSelection(...).chartProps`. Spread for callback wiring. Optional —
	 * surfaces that don't react to selection (the seats accordion's static/edit-channels
	 * view) can omit this.
	 */
	chartProps?: SeatsSelectionChartProps;
	/**
	 * Per-object colour override. When provided, the widget asks this for every rendered
	 * object — return the `defaultColor` to keep the seats.io-assigned colour, or any other
	 * string (e.g. `'#cccccc'`) to grey the object out. Use for visual-cue gating when
	 * the user has declared their target category up-front.
	 */
	objectColor?: SeatsObjectColorFn;
	/**
	 * Per-object selectability. **Honoured only in `select` mode.** Returning `false`
	 * prevents the user from selecting that object at the SDK level — the cleanest way
	 * to constrain selection to a single category in Send Tickets / Comp Tickets flows.
	 * Combine with `objectColor` for both the visual cue and the hard gate.
	 */
	isObjectSelectable?: SeatsIsObjectSelectableFn;
}

/**
 * PunchupSeatsWidget
 *
 * Single contact point with `<SeatsioEventManager>` for all green-zone surfaces. Centralises:
 * - The branded `colors` and `style` overrides (`seatsTheme.ts`).
 * - The `colorScheme="light"` pin and language coercion.
 * - The partner-secret short-circuit: returns `null` when `partnerData.seats_io_secret_key`
 *   is missing, so the consumer never has to repeat the guard.
 * - The min-height container that prevents chart-load layout flicker.
 * - Optional `objectColor` (any mode) and `isObjectSelectable` (`select` mode only) hooks
 *   for category-filtering.
 *
 * Returns `null` when the event key is falsy or partner data lacks the secret key. The
 * consumer is responsible for any surrounding "credentials missing" messaging — this
 * component just suppresses the render so the rest of the page is unaffected.
 *
 * **Why a composite, not a primitive:** it reads partner data via a hook (`useGetPartnerData`)
 * and depends on the i18n context. Primitives are pure props-in / render-out.
 */
export const PunchupSeatsWidget: FC<PunchupSeatsWidgetProps> = ({
	eventKey,
	mode,
	chartProps,
	objectColor,
	isObjectSelectable,
}) => {
	const { i18n } = useTranslation();
	const { partnerData } = useGetPartnerData();

	if (!eventKey || !partnerData?.seats_io_secret_key) {
		return null;
	}

	const region = toSeatsRegion(partnerData.seats_region);

	// `isObjectSelectable` is only honoured by seats.io in `select` mode — passing it on
	// other modes is silently ignored. We narrow at the boundary so the runtime doesn't
	// dispatch a no-op per render and so the type union below is unambiguous for consumers.
	const selectModeProps = mode === 'select' && isObjectSelectable ? { isObjectSelectable } : undefined;

	return (
		<div className={styles.container}>
			<SeatsioEventManager
				secretKey={partnerData.seats_io_secret_key}
				event={eventKey}
				region={region}
				mode={mode}
				colorScheme="light"
				language={toSeatsLanguage(i18n.language)}
				colors={SEATS_COLORS}
				style={SEATS_STYLE}
				{...(objectColor ? { objectColor } : undefined)}
				{...selectModeProps}
				{...chartProps}
			/>
		</div>
	);
};
